import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'
import { JEPX_AREA_CODES, parseJepxAreaCodes } from '~/models/area'

/** 札を作るモーダルが前回選んだエリアを持つ市場。 保存先はスポットと時間前で分ける (#10105)。 */
export type BidMarket = 'spot' | 'intraday'

const STORAGE_KEYS: Record<BidMarket, string> = {
  spot: 'ebalance-spot-last-bid-areas',
  intraday: 'ebalance-intraday-last-bid-areas',
}

export const readLastBidAreas = (market: BidMarket): AreaCode[] => {
  const raw = getLocalStorageItem(STORAGE_KEYS[market])
  return raw === undefined ? [] : (parseJepxAreaCodes(raw) ?? [])
}

export const saveLastBidAreas = (
  market: BidMarket,
  areas: readonly AreaCode[],
): void => {
  const distinct = JEPX_AREA_CODES.filter((code) => areas.includes(code))
  if (distinct.length === 0) return
  setLocalStorageItem(STORAGE_KEYS[market], JSON.stringify(distinct))
}

const initialAreaCandidates = (
  market: BidMarket,
  filterArea: AreaCode | undefined,
): AreaCode[] =>
  filterArea !== undefined ? [filterArea] : readLastBidAreas(market)

/**
 * 札を作るモーダルの単一選択のエリア欄の初期値 (AC-BSP-123 / AC-BIN-122)。 フィルタで 1 エリアなら
 * そのエリア、 それ以外は前回保存した札のエリアが 1 つの時のそのエリア。 `isSelectable` が偽なら空欄。
 *
 * `isSelectable` はモーダルを開いた時点の選択肢で判定する。 発注主体区分を未選択で開くモーダルでは、
 * 区分での判定は既定選択の時の選択解除 (AC-BSP-005b / AC-BIN-004 ※) が担う。 スポットの個別作成と
 * ブロック作成は、 解除が欄の初期化と前後して効かないことがあるため、 既定選択される区分
 * ([[settledIssuerOption]]) でも判定する (#10105)。
 */
export const initialBidArea = (
  market: BidMarket,
  filterArea: AreaCode | undefined,
  isSelectable: (area: AreaCode) => boolean,
): AreaCode | undefined => {
  const candidates = initialAreaCandidates(market, filterArea)
  const area = candidates.length === 1 ? candidates[0] : undefined
  return area !== undefined && isSelectable(area) ? area : undefined
}

/** 複数選択のエリア欄の初期値。 [[initialBidArea]] の候補のうち `isSelectable` が真のものすべて。 */
export const initialBidAreas = (
  market: BidMarket,
  filterArea: AreaCode | undefined,
  isSelectable: (area: AreaCode) => boolean,
): AreaCode[] => initialAreaCandidates(market, filterArea).filter(isSelectable)
